import {
    Button,
    Dialog,
    DialogActions,
    DialogBody,
    DialogContent,
    DialogSurface,
    DialogTitle,
    DialogTrigger,
    useId,
} from '@fluentui/react-components';
import { useOnEvent } from '@fluentui/react-hooks';
import { CheckmarkRegular, DismissRegular } from '@fluentui/react-icons';
import { memo, useCallback, useState } from 'react';

interface IConfirmationDialogProps {
    confirmFn: () => void;
    dismissFn?: () => void;
    confirmText?: string;
    content: string;
    secondaryConfirm?: {
        confirmFn: () => void;
        confirmText: string;
    };
    title: string;
}

type TCustomEvent = CustomEvent<IConfirmationDialogProps>;

const eventName = 'confDlg';

export const ConfirmationDialog = memo(() => {
    const dlgId = useId('dialog-');
    const dlgTitleId = dlgId + '-title';
    const dlgContentId = dlgId + '-content';
    const [props, setProps] = useState<IConfirmationDialogProps>();

    useOnEvent<HTMLBodyElement, TCustomEvent>(
        document,
        eventName,
        useCallback((event) => {
            setProps(event.detail);
        }, [])
    );

    // TODO: hide (setProps()) dialog when navigating...

    return (
        <Dialog
            onOpenChange={(_event, data) => {
                if (!data.open) {
                    setProps(undefined);
                }
            }}
            open={!!props}
            surfaceMotion={null}
        >
            <DialogSurface aria-labelledby={dlgTitleId} aria-describedby={dlgContentId}>
                {props && (
                    <DialogBody>
                        <DialogTitle id={dlgTitleId}>{props.title}</DialogTitle>

                        <DialogContent id={dlgContentId} style={{ whiteSpace: 'pre-line' }}>
                            {props.content}
                        </DialogContent>

                        <DialogActions>
                            <DialogTrigger disableButtonEnhancement>
                                <Button
                                    appearance="primary"
                                    icon={<CheckmarkRegular />}
                                    onClick={() => {
                                        props.confirmFn();
                                    }}
                                >
                                    {props.confirmText || 'Bestätigen'}
                                </Button>
                            </DialogTrigger>

                            {props.secondaryConfirm && (
                                <DialogTrigger disableButtonEnhancement>
                                    <Button
                                        appearance="secondary"
                                        icon={<CheckmarkRegular />}
                                        onClick={() => {
                                            props.secondaryConfirm!.confirmFn();
                                        }}
                                        style={{ whiteSpace: 'nowrap' }}
                                    >
                                        {props.secondaryConfirm.confirmText}
                                    </Button>
                                </DialogTrigger>
                            )}

                            <DialogTrigger disableButtonEnhancement>
                                <Button
                                    appearance="secondary"
                                    icon={<DismissRegular />}
                                    onClick={() => {
                                        props?.dismissFn?.();
                                    }}
                                >
                                    Abbrechen
                                </Button>
                            </DialogTrigger>
                        </DialogActions>
                    </DialogBody>
                )}
            </DialogSurface>
        </Dialog>
    );
});

export const triggerConfirmationDialog = (detail: IConfirmationDialogProps): void => {
    document.dispatchEvent(new CustomEvent(eventName, { detail }));
};
